import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CommonState } from "../../../types/state/common";
import {
  callPhpWorkflow,
  getDepartments,
  getSites,
  getSitesV2,
  getEmployeeSites,
  getAssignedTimeoffCategories,
  getUnreadNotifCount,
  getAllTimeoffCategories,
  fetchLastPunch,
  getJobCodes,
  getShifts,
  getGroups,
  fetchAttendanceTags,
  fetchAttendanceTagsV2,
  fetchScheduleAttendanceTags,
  fetchSkipMealBreakStatus,
  fetchEmployees,
  getEmployees,
  getFlexFieldValue,
} from "./api";
import { TimeoffCategoryType } from "../../../types";

const initialState: CommonState = {
  sites: [],
  sitesV2: [],
  departments: [],
  jobCodes: [],
  shifts: [],
  groups: [],
  attendanceTags: [],
  attendanceTagsV2: [],
  isLoadingAttendanceTags: false,
  LastPunchState: null,
  assignedTimeoffCategories: [],
  isLoadingAssignedCategories: false,
  isCallingWorkflow: false,
  allTimeoffCategories: [],
  isLoadingAllCategories: false,

  // unread notification count
  isLoadingUnreadNotifCount: false,
  unreadNotifCount: 0,
  employeeList: [],
  skipMealBreakStatusData: null,
  isLoadingSkipMealBreakStatus: false,
  skipMealBreakStatusLastFetched: null,
  skipMealBreakStatusError: null,
  
  // Flex field values
  flexFieldValues: {},
  flexFieldLoading: {},
};

export const CommonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    clearSites: (state) => {
      state.sites = [];
    },

    decrementUnreadNotifCount: (state) => {
      if (state.unreadNotifCount > 0) {
        state.unreadNotifCount -= 1;
      }
    },
  },
  extraReducers: {
    // get sites
    [getSites.fulfilled.type]: (state, { payload }) => {
      if (payload.data) {
        state.sites = payload.data;
      }
    },

    [getSites.rejected.type]: (state) => {
      state.sites = [];
    },

    [getSites.pending.type]: (state) => {
      state.sites = [];
    },

    // get sites v2
    [getSitesV2.fulfilled.type]: (state, { payload }) => {
      if (payload.data) {
        state.sitesV2 = payload.data;
      }
    },

    [getSitesV2.rejected.type]: (state) => {
      state.sitesV2 = [];
    },

    [getSitesV2.pending.type]: (state) => {
      state.sitesV2 = [];
    },

    // get employee sites
    [getEmployeeSites.fulfilled.type]: (state, { payload }) => {
      if (payload.data) {
        state.sites = payload.data;
      }
    },

    [getEmployeeSites.rejected.type]: (state) => {
      state.sites = [];
    },

    [getEmployeeSites.pending.type]: (state) => {
      state.sites = [];
    },

    // get departments
    [getDepartments.fulfilled.type]: (state, { payload }) => {
      if (payload.data) {
        state.departments = payload.data;
      }
    },

    [getDepartments.rejected.type]: (state) => {
      state.departments = [];
    },

    [getDepartments.pending.type]: (state) => {
      state.departments = [];
    },
    // getJobCodes
    [getJobCodes.fulfilled.type]: (state, { payload }) => {
      if (payload.data) {
        state.jobCodes = payload.data;
      }
    },

    [getJobCodes.rejected.type]: (state) => {
      state.jobCodes = [];
    },

    [getJobCodes.pending.type]: (state) => {
      state.jobCodes = [];
    },
    // getShifts
    [getShifts.fulfilled.type]: (state, { payload }) => {
      if (payload.data) {
        state.shifts = payload.data;
      }
    },

    [getShifts.rejected.type]: (state) => {
      state.shifts = [];
    },

    [getShifts.pending.type]: (state) => {
      state.shifts = [];
    },
    // getGroups
    [getGroups.fulfilled.type]: (state, { payload }) => {
      if (payload.data) {
        state.groups = payload.data;
      }
    },

    [getGroups.rejected.type]: (state) => {
      state.groups = [];
    },

    [getGroups.pending.type]: (state) => {
      state.groups = [];
    },
    // attendanceTags
    [fetchAttendanceTags.fulfilled.type]: (state, { payload }) => {
      if (payload.data) {
        state.attendanceTags = payload.data;
      }
    },

    [fetchAttendanceTags.rejected.type]: (state) => {
      state.attendanceTags = [];
    },

    [fetchAttendanceTags.pending.type]: (state) => {
      state.attendanceTags = [];
    },

    // attendanceTagsV2
    [fetchAttendanceTagsV2.fulfilled.type]: (state, { payload }) => {
      if (payload.data) {
        state.attendanceTagsV2 = payload.data;
      }
    },

    [fetchAttendanceTagsV2.rejected.type]: (state) => {
      state.attendanceTagsV2 = [];
    },

    [fetchAttendanceTagsV2.pending.type]: (state) => {
      state.attendanceTagsV2 = [];
    },

    // fetchScheduleAttendanceTags - uses same state as attendanceTags
    [fetchScheduleAttendanceTags.fulfilled.type]: (state, { payload }) => {
      if (payload.data) {
        state.attendanceTags = payload.data;
      }
      state.isLoadingAttendanceTags = false;
    },

    [fetchScheduleAttendanceTags.rejected.type]: (state) => {
      state.attendanceTags = [];
      state.isLoadingAttendanceTags = false;
    },

    [fetchScheduleAttendanceTags.pending.type]: (state) => {
      state.isLoadingAttendanceTags = true;
    },

    // get assigned time off categories
    [getAssignedTimeoffCategories.fulfilled.type]: (
      state,
      action: PayloadAction<TimeoffCategoryType[]>
    ) => {
      state.isLoadingAssignedCategories = false;
      state.assignedTimeoffCategories = action.payload;
    },

    [getAssignedTimeoffCategories.rejected.type]: (state) => {
      state.isLoadingAssignedCategories = false;
      state.assignedTimeoffCategories = [];
    },

    [getAssignedTimeoffCategories.pending.type]: (state) => {
      state.isLoadingAssignedCategories = true;
      state.assignedTimeoffCategories = [];
    },

    // get all time off categories
    [getAllTimeoffCategories.fulfilled.type]: (state, { payload }) => {
      state.isLoadingAllCategories = false;
      state.allTimeoffCategories = payload;
    },

    [getAllTimeoffCategories.rejected.type]: (state) => {
      state.isLoadingAllCategories = false;
      state.allTimeoffCategories = [];
    },

    [getAllTimeoffCategories.pending.type]: (state) => {
      state.isLoadingAllCategories = true;
      state.allTimeoffCategories = [];
    },

    [callPhpWorkflow.pending.type]: (state) => {
      state.isCallingWorkflow = true;
    },

    [callPhpWorkflow.fulfilled.type]: (state) => {
      state.isCallingWorkflow = false;
    },

    [callPhpWorkflow.rejected.type]: (state) => {
      state.isCallingWorkflow = false;
    },

    [getUnreadNotifCount.fulfilled.type]: (
      state,
      action: PayloadAction<number>
    ) => {
      state.isLoadingUnreadNotifCount = false;
      state.unreadNotifCount = action.payload;
    },

    [getUnreadNotifCount.rejected.type]: (state) => {
      state.isLoadingUnreadNotifCount = false;
    },

    [getUnreadNotifCount.pending.type]: (state) => {
      state.isLoadingUnreadNotifCount = true;
    },
    [fetchLastPunch.fulfilled.type]: (state, { payload }) => {
      state.LastPunchState = payload;
    },

    [fetchLastPunch.rejected.type]: (state) => {
      state.LastPunchState = null;
    },

    [fetchLastPunch.pending.type]: (state) => {
      state.LastPunchState = null;
    },

    [fetchSkipMealBreakStatus.fulfilled.type]: (state, { payload }) => {
      state.skipMealBreakStatusData = payload.data || null;
      state.isLoadingSkipMealBreakStatus = false;
      state.skipMealBreakStatusLastFetched = Date.now();
      state.skipMealBreakStatusError = null;
    },

    [fetchSkipMealBreakStatus.rejected.type]: (state, action) => {
      state.skipMealBreakStatusData = null;
      state.isLoadingSkipMealBreakStatus = false;
      state.skipMealBreakStatusLastFetched = null;
      state.skipMealBreakStatusError =
        (typeof action.payload === "string" && action.payload.trim()) ||
        "Couldn't load skip meal break status.";
    },

    [fetchSkipMealBreakStatus.pending.type]: (state) => {
      state.isLoadingSkipMealBreakStatus = true;
      state.skipMealBreakStatusError = null;
    },

    [fetchEmployees.fulfilled.type]: (state, { payload }) => {
      // fetchEmployees returns EmployeesApiResponse (array directly)
      // Don't update employeeList since it expects Employee[] type
      // This is kept for backward compatibility but doesn't update employeeList
    },

    [fetchEmployees.rejected.type]: (state) => {
      // No-op
    },

    [fetchEmployees.pending.type]: (state) => {
      // No-op
    },

    [getEmployees.fulfilled.type]: (state, { payload }) => {
      state.employeeList = payload.data || [];
    },

    [getEmployees.rejected.type]: (state) => {
      state.employeeList = [];
    },

    [getEmployees.pending.type]: (state) => {
      // Don't clear the list while loading
    },

    // Flex Field Value
    [getFlexFieldValue.pending.type]: (state, action) => {
      const fieldCode = action.meta.arg.field_code;
      state.flexFieldLoading[fieldCode] = true;
    },

    [getFlexFieldValue.fulfilled.type]: (state, action) => {
      const { fieldCode, data } = action.payload;
      state.flexFieldValues[fieldCode] = data;
      state.flexFieldLoading[fieldCode] = false;
    },

    [getFlexFieldValue.rejected.type]: (state, action) => {
      const fieldCode = action.meta.arg.field_code;
      state.flexFieldValues[fieldCode] = null;
      state.flexFieldLoading[fieldCode] = false;
    },
  },
});

export const { clearSites, decrementUnreadNotifCount } = CommonSlice.actions;
export default CommonSlice.reducer;
